import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Heart, MessageCircle, Smile, Star, ThumbsUp, Volume2, VolumeX } from "lucide-react";
import { SectionHeader } from "@/components/Primitives";
import { Reveal } from "@/components/Reveal";

/**
 * TikTok-style UGC creator reel. Four portrait videos scroll horizontally
 * on mobile (snap) and sit side-by-side on desktop. Each card has
 * animated 5-star rating, floating reaction counters, and auto-plays
 * (muted) when scrolled into view. Tap to unmute, tap again to mute.
 */

const REELS = [
  {
    id: 1,
    src: "/videos/ugc-1.mp4",
    poster: "/videos/ugc-1.jpg",
    caption: "Stuck one on the trailer drawbar — 60-second setup, no cables, no app.",
    stats: { likes: "12.4K", loves: "3.1K", laughs: "842", comments: "1.2K" },
  },
  {
    id: 2,
    src: "/videos/ugc-2.mp4",
    poster: "/videos/ugc-2.jpg",
    caption: "This is the magnetic tracker everyone's been asking about 🧲",
    stats: { likes: "28.7K", loves: "6.4K", laughs: "1.8K", comments: "2.9K" },
  },
  {
    id: 3,
    src: "/videos/ugc-3.mp4",
    poster: "/videos/ugc-3.jpg",
    caption: "Hidden under the chassis. Found it from the pub, 40km away.",
    stats: { likes: "9.8K", loves: "2.3K", laughs: "612", comments: "847" },
  },
  {
    id: 4,
    src: "/videos/ugc-4.mp4",
    poster: "/videos/ugc-4.jpg",
    caption: "Fits the toolbox, bike, trailer — same tag, moves everywhere.",
    stats: { likes: "15.2K", loves: "4.0K", laughs: "923", comments: "1.6K" },
  },
];

const AnimatedStars = ({ rating = 4.8, delay = 0 }) => {
  const filled = Math.floor(rating);
  const partial = rating - filled;
  return (
    <div className="flex items-center gap-0.5">
      {[0, 1, 2, 3, 4].map((i) => {
        const fillPct = i < filled ? 1 : i === filled ? partial : 0;
        return (
          <motion.span
            key={i}
            initial={{ scale: 0, rotate: -40, opacity: 0 }}
            animate={{ scale: 1, rotate: 0, opacity: 1 }}
            transition={{ delay: delay + i * 0.08, type: "spring", stiffness: 420, damping: 14 }}
            className="relative inline-block"
          >
            <Star className="h-4 w-4 text-white/25" strokeWidth={0} fill="currentColor" />
            {fillPct > 0 && (
              <span
                className="pointer-events-none absolute left-0 top-0 overflow-hidden"
                style={{ width: `${fillPct * 100}%` }}
              >
                <Star className="h-4 w-4 text-yellow-300 drop-shadow-[0_0_4px_rgba(253,224,71,0.6)]" strokeWidth={0} fill="currentColor" />
              </span>
            )}
          </motion.span>
        );
      })}
    </div>
  );
};

const Reaction = ({ icon: Icon, count, color, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay, duration: 0.4 }}
    className="flex items-center gap-1 rounded-full bg-black/35 px-2 py-1 text-[10px] font-bold text-white backdrop-blur-md sm:text-xs"
  >
    <Icon className="h-3 w-3 sm:h-3.5 sm:w-3.5" style={{ color }} fill={color} />
    <span>{count}</span>
  </motion.div>
);

const Reel = ({ reel, index }) => {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const inView = useInView(ref, { margin: "-15% 0px" });
  const [muted, setMuted] = useState(true);

  // Autoplay (muted) when scrolled into view, pause when out
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (inView) {
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  }, [inView]);

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    // On first unmute attempt, also ensure it's playing
    if (!v.muted && v.paused) v.play().catch(() => {});
  };

  return (
    <motion.article
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ delay: index * 0.1, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`ugc-reel-${reel.id}`}
      className="relative shrink-0 snap-start overflow-hidden rounded-[1.4rem] bg-tracksy-ink shadow-lift w-[72vw] max-w-[260px] sm:w-[220px] md:w-[240px] lg:w-full lg:max-w-none"
      style={{ aspectRatio: "9/16" }}
    >
      {/* Video */}
      <video
        ref={videoRef}
        src={reel.src}
        poster={reel.poster}
        muted={muted}
        loop
        playsInline
        preload="metadata"
        onClick={toggleMute}
        className="absolute inset-0 h-full w-full cursor-pointer object-cover"
        data-testid={`ugc-reel-video-${reel.id}`}
      />

      {/* Top gradient wash */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/55 via-black/15 to-transparent" />

      {/* Mute toggle */}
      <button
        type="button"
        onClick={toggleMute}
        aria-label={muted ? "Unmute video" : "Mute video"}
        className="absolute right-2.5 top-2.5 inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur-md transition-colors hover:bg-black/60"
      >
        {muted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
      </button>

      {/* Stars pill at top-left */}
      <div className="absolute left-2.5 top-2.5">
        <div className="flex items-center gap-1 rounded-full bg-black/40 px-2 py-1 backdrop-blur-md">
          <AnimatedStars rating={4.8} delay={index * 0.1 + 0.2} />
          <span className="pl-1 text-[10px] font-bold text-white">4.8</span>
        </div>
      </div>

      {/* Bottom caption + reactions */}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/50 to-transparent px-3 pb-3 pt-10">
        <p className="line-clamp-2 text-[11px] font-semibold leading-snug text-white sm:text-xs">
          {reel.caption}
        </p>
        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
          <Reaction icon={ThumbsUp} count={reel.stats.likes} color="#60a5fa" delay={index * 0.1 + 0.35} />
          <Reaction icon={Heart} count={reel.stats.loves} color="#f87171" delay={index * 0.1 + 0.42} />
          <Reaction icon={Smile} count={reel.stats.laughs} color="#fbbf24" delay={index * 0.1 + 0.49} />
          <Reaction icon={MessageCircle} count={reel.stats.comments} color="#ffffff" delay={index * 0.1 + 0.56} />
        </div>
      </div>

      {/* Playing — intentionally no "LIVE" badge */}
    </motion.article>
  );
};

const UgcReels = () => (
  <section
    id="ugc-reels"
    data-testid="ugc-reels-section"
    className="scroll-mt-24 px-4 py-14 sm:px-6 sm:py-20 lg:px-8"
  >
    <div className="mx-auto max-w-7xl">
      <Reveal>
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
          <SectionHeader
            eyebrow="As seen on TikTok"
            title="4.8 ★ · Real people. Real trackers."
            desc="Thousands of creators are stashing a Tracksy on their ute, trailer, toolbox and luggage. Scroll the latest."
            className="max-w-xl"
          />
          <div className="hidden items-center gap-2 rounded-full bg-tracksy-cream px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-tracksy-ink sm:inline-flex">
            <AnimatedStars rating={4.8} />
            <span className="pl-1">4.8 · 2,100+ reviews</span>
          </div>
        </div>
      </Reveal>

      {/* Scrollable rail — snap on mobile, 4-col grid on desktop */}
      <div className="mt-8">
        <div className="-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-4 scroll-smooth sm:gap-4 lg:mx-0 lg:grid lg:snap-none lg:grid-cols-4 lg:gap-5 lg:overflow-visible lg:px-0 lg:pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {REELS.map((r, i) => (
            <Reel key={r.id} reel={r} index={i} />
          ))}
        </div>
      </div>

      {/* Mobile star summary (shown when desktop pill is hidden) */}
      <div className="mt-4 flex items-center justify-center gap-2 sm:hidden">
        <AnimatedStars rating={4.8} />
        <span className="text-xs font-bold text-tracksy-ink">4.8 · 2,100+ reviews</span>
      </div>
    </div>
  </section>
);

export default UgcReels;
